import { addToJobQueue, clearJobQueue } from "slices/app/appSlice";
import localhost, {
  downloadFileFromResponse,
  downloadWithIFrame,
} from "apis/localhost";
import { showErrorModal } from "slices/app/actions/error";
import { exportOrigin, folderBy } from "layout/dictionarySearch/constants";
import { pollJobStatus } from "slices/discovery/actions/theme";
import { hideModal } from "slices/app/actions/modals";
import { modalTypes } from "constants/types";
import { saveToLS } from "@visiblethread/react-components/dist/utils";
import { DS_EXPORT_CONTENT_CLSX_LS_KEY } from "constants/dictionarySearch";

const jobStatusInterval = 3000;

export const exportAllPdfFolder = () => async (dispatch, getState) => {
  dispatch(addToJobQueue("genericJob"));

  const { folderId } = getState().dictionarySearch.config;
  const formData = new FormData();
  formData.append("docSetId", folderId);

  await localhost
    .post("/docs/dictionarySearch/export/pdf/folder", formData)
    .then(async (response) => {
      const { data } = response;
      downloadWithIFrame(data);
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const exportAllPdfDocument = () => async (dispatch, getState) => {
  dispatch(addToJobQueue("genericJob"));

  const { folderId, documentId } = getState().dictionarySearch.config;
  const formData = new FormData();
  formData.append("docSetId", folderId);
  formData.append("documentId", documentId);

  await localhost
    .post("/docs/dictionarySearch/export/pdf/document", formData)
    .then(async (response) => {
      const { data } = response;
      downloadWithIFrame(data);
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const getJobStatusExports = (jobId) => async (dispatch) => {
  dispatch(addToJobQueue("genericJob"));

  await localhost
    .get("docs/jobStatus", {
      params: { jobId },
    })
    .then((response) => {
      const { data } = response;
      if (data.status === "RUNNING") {
        setTimeout(() => {
          dispatch(getJobStatusExports(jobId));
        }, jobStatusInterval);
      } else if (data.status === "ERROR") {
        dispatch(showErrorModal({ message: data.jobMetaInfo.error }));
        dispatch(clearJobQueue());
      } else {
        downloadWithIFrame(data.jobMetaInfo.file);
        dispatch(clearJobQueue());
      }
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
      dispatch(clearJobQueue());
    });
};

export const exportAnnotatedDocument =
  (selectedTerms, categoryAsCommentAuthor) => async (dispatch, getState) => {
    dispatch(addToJobQueue("genericJob"));
    const { documentId } = getState().dictionarySearch.config;

    await localhost
      .post("/docs/dictionarySearch/export/annotatedDocument", {
        documentId,
        selectedTerms,
        categoryAsCommentAuthor,
      })
      .then(async (response) => {
        const { data } = response;
        dispatch(getJobStatusExports(data.jobId));
      })
      .catch((error) => {
        dispatch(showErrorModal(error));
      });
  };

const getExportUrlEnding = (origin) => {
  if (origin === exportOrigin.catShred) {
    return "/categoryShred";
  }
  if (origin === exportOrigin.simpleShred) {
    return "/simpleShred";
  }
  return "/content/excel";
};

const getExportUrl = (by, folderId, documentId, origin) => {
  let url = documentId === undefined ? "/folder" : "/document";
  if (documentId === undefined) {
    url += by === folderBy.folder ? "/folder" : "/document";
  }
  url += getExportUrlEnding(origin);
  return url;
};

export const exportXlsx =
  (documentsWithTerms, shredOptionsRequest, origin) =>
  async (dispatch, getState) => {
    dispatch(addToJobQueue("genericJob"));
    const { folderId, documentId } = getState().dictionarySearch.config;
    const by = getState().dictionarySearch.config.folderBy;
    await localhost
      .post(
        `/docs/dictionarySearch/export${getExportUrl(
          by,
          folderId,
          documentId,
          origin
        )}`,
        {
          folderId,
          documentsWithTerms,
          shredOptionsRequest,
        }
      )
      .then(async (response) => {
        dispatch(hideModal(modalTypes.EXPORT_CONTENT));
        const jobData = response.data.jobId;
        dispatch(pollJobStatus(jobData));
        dispatch(addToJobQueue("genericJob"));
        saveToLS(
          DS_EXPORT_CONTENT_CLSX_LS_KEY,
          JSON.stringify(shredOptionsRequest)
        );
      })
      .catch((error) => {
        dispatch(showErrorModal(error));
        dispatch(clearJobQueue());
      });
  };

export const exportContentCSV =
  (name, isDocView, docList) => async (dispatch, getState) => {
    const { folderId, folderBy: by } = getState().dictionarySearch.config;
    let url = "/docs/dictionarySearch/export/content/csv";
    if (isDocView) {
      url += "/document";
    } else if (by === folderBy.folder) {
      url += "/folder";
    } else {
      url += "/folder/document";
    }
    dispatch(addToJobQueue("genericJob"));
    await localhost
      .post(
        url,
        {
          docSetId: folderId,
          docList,
        },
        {
          responseType: "blob",
        }
      )
      .then(async (response) => {
        const { data } = response;
        downloadFileFromResponse(
          data,
          `Dictionary Search Content - ${name}.csv`
        );
      })
      .catch((error) => {
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(clearJobQueue());
      });
  };

export const exportResultsCSVFolder =
  (name, selectedTerms) => async (dispatch, getState) => {
    dispatch(addToJobQueue("genericJob"));
    const { folderId } = getState().dictionarySearch.config;

    await localhost
      .post(
        "/docs/dictionarySearch/export/result/csv/folder",
        { folderId, selectedTerms },
        { responseType: "blob" }
      )
      .then(async (response) => {
        const { data } = response;
        downloadFileFromResponse(
          data,
          `Dictionary Search Results - ${name}.csv`
        );
      })
      .catch((error) => {
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(clearJobQueue());
      });
  };

export const exportResultsCSVDocument =
  (docName, selectedTerms) => async (dispatch, getState) => {
    dispatch(addToJobQueue("genericJob"));
    const { folderId, documentId } = getState().dictionarySearch.config;

    await localhost
      .post(
        "/docs/dictionarySearch/export/result/csv/document",
        { folderId, documentId, selectedTerms },
        { responseType: "blob" }
      )
      .then(async (response) => {
        const { data } = response;
        downloadFileFromResponse(
          data,
          `Dictionary Search Results - ${docName}.csv`
        );
      })
      .catch((error) => {
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(clearJobQueue());
      });
  };
